import { CustomClaimValue, useAuth } from '@app/shared';
import cn from 'classnames';
import React, { ReactNode, useEffect, useRef, useState, } from 'react';
import { useMedia } from 'react-use';

export interface TabProps {
  title: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}

interface TabContainerProps {
  onTabChange?: (id: string | number) => void;
  children: ReactNode;
  className?: string;
  childrenClassName?: string;
  titleRight?: string | ReactNode;
}

const Tab: React.FC<TabProps> = ({ children, className }) => {
  return <div className={className ? className : "py-6 px-4"}>{children}</div>;
};
const TabSpacer: React.FC<{ additionalClasses?: string }> = ({ additionalClasses }) => {
  return <div className={`w-px h-10 bg-gray-300 mx-54 mt-4 self-center ${additionalClasses ?? ''}`} />;
};

const ClaimsRequiredTab: React.FC<{ claims: CustomClaimValue | CustomClaimValue[] } & TabProps> = ({
  claims,
  ...props
}) => {
  const user = useAuth();
  const normalizedClaims = Array.isArray(claims) ? claims : [claims]; // Ensure it's always an array

  if (normalizedClaims.every(claim => user?.userClaims?.find(uc => uc.claims.find(c => c === claim)) ?? false)) {
    return <Tab {...props} />;
  };
  return null;
}




const TabContainer: React.FC<TabContainerProps> = ({ children, titleRight, onTabChange, ...props }) => {
  const [activeTab, setActiveTab] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const underlineRef = useRef<HTMLDivElement | null>(null);
  const isLarge = useMedia('(min-width: 1024px)');
  const isXl = useMedia('(min-width: 1280px)');

  const allChildren = flattenChildren(children);

  const tabChildren = allChildren.filter(
    (child) => child.type !== TabSpacer
  ) as React.ReactElement<TabProps>[];

  const handleTabChange = (index: number) => {
    if (index === activeTab) return;
    setActiveTab(index);
    onTabChange?.(tabChildren[index].props.title);
  };

  const updateUnderline = () => {
    const el = tabRefs.current[activeTab];
    const underline = underlineRef.current;
    if (el && underline) {
      const rect = el.getBoundingClientRect();
      const parentRect = el.parentElement!.getBoundingClientRect();

      underline.style.width = `${rect.width}px`;
      underline.style.left = `${rect.left - parentRect.left - (isXl ? 31 : isLarge ? 12 : -1)}px`;
      underline.style.top = `${el.offsetTop + el.offsetHeight + 10}px`; // between rows
    }
  };

  useEffect(() => {

    updateUnderline();
    window.addEventListener("resize", updateUnderline);
    window.addEventListener("orientationchange", updateUnderline);
    window.addEventListener("focus", updateUnderline);

    return () => {
      window.removeEventListener("resize", updateUnderline);
      window.removeEventListener("orientationchange", updateUnderline);
      window.removeEventListener("focus", updateUnderline);
    };
  }, [activeTab, isLarge, isXl]);

  return (
    <div className={cn(props.className, 'w-full h-full flex flex-col overflow-hidden')}>
      <div className="relative flex justify-between items-center mb-5 flex-wrap">
        <div className="flex gap-x-4 lg:gap-x-0 lg:space-x-3 xl:space-x-8 flex-wrap items-center">
          {allChildren.map((child, index) => {
            if (child.type === TabSpacer) return child;

            const tabIndex = tabChildren.findIndex((t) => t === child);
            if (tabIndex === -1) return null;


            return (
              <button
                key={index}
                disabled={tabChildren[tabIndex].props.disabled}
                ref={(el) => (tabRefs.current[tabIndex] = el)}
                className="relative py-4 px-1 border-none focus:outline-none focus:ring-0 bg-transparent"
                onClick={() => handleTabChange(tabIndex)}
              >
                <span className={`text-lg md:text-xl ${activeTab === tabIndex ? 'font-bold' : 'font-semibold '} ${child.props.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-link-left-nav-hover'}`}              >
                  {child.props.title}
                </span>
              </button>
            );
          })}

          <div
            ref={underlineRef}
            className="absolute bg-background-brand-primary h-[3px] rounded-full transition-all duration-3000 ease-in-out"
          />
        </div>
        {titleRight && <div className="me-2 flex items-center mt-5">{titleRight}</div>}
      </div>

      <div className={cn(
        props.childrenClassName ?? 'relative mt-4 flex-1 min-h-0 overflow-auto'
      )}>
        {tabChildren.map((child, index) => (
          <div
            key={index}
            className={`${activeTab === index ? 'block' : 'hidden'} h-full`} 
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  );
};

function flattenChildren(children: ReactNode): React.ReactElement[] {
  return React.Children.toArray(children).flatMap((child) => {
    if (Array.isArray(child)) {
      return flattenChildren(child);
    }
    if (React.isValidElement(child)) {
      return [child];
    }
    return [];
  });
}

export { ClaimsRequiredTab, Tab, TabContainer, TabSpacer };
